import type { FilterDef } from "./filterDefs";
import type { SelectedFilters } from "./useSelectedFilters";

/**
 * Turning the user's selection into the backend's `filter` parameters.
 *
 * Replaces the legacy `buildDatasetQuery`, which assembled a nested
 * `['and', ['match', field, …]]` tree on the client. The grammar is flat now —
 * `field:eq:value` and `field:range:min,max` — and the server builds the tree
 * (see `packages/backend/src/features/items/items.filters.ts`).
 */
export function filterParams(
  selected: SelectedFilters,
  defs: FilterDef[],
  /** Omit this filter's own selection — used when facetting it under `or`. */
  exclude?: string
): string[] {
  const params: string[] = [];

  for (const def of defs) {
    if (def.name === exclude) continue;

    const values = selected[def.name];
    if (!values?.length) continue;

    if (def.kind === "simple") {
      for (const value of values) params.push(`${def.field}:eq:${value}`);
      continue;
    }

    // A range selection is [min, max]; either bound may be blank for an open end.
    const [min = "", max = ""] = values;
    if (min || max) params.push(`${def.field}:range:${min},${max}`);
  }

  return params;
}

/**
 * Whether a filter's own selection is left out of its own facet counts.
 *
 * Under `or`, selecting "museum" must not reduce the museum filter's other
 * options to zero — the user is choosing *among* them, so its counts are taken
 * as if nothing in it were selected. Under `and`, each further choice genuinely
 * narrows, so the selection stays in.
 */
export function excludesOwnSelection(logic: string | undefined): boolean {
  return logic === "or";
}
